import Document from "@tiptap/extension-document";
import History from "@tiptap/extension-history";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { useEditor } from "@tiptap/react";
import clsx from "clsx";
import { useEffect } from "react";

import AiVariableExtension from "features/templates/components/TemplateEditor/AiVariableExtension";
import VariableExtension from "features/templates/components/TemplateEditor/VariableExtension";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";
import {
  areObjectsEqual,
  extractAiDescriptors,
  parseTemplateText,
  stringifyTemplateContent,
} from "features/templates/utils";

import type { UseFormReturn } from "react-hook-form";
import type { DetailedCampaign } from "../../../pages/campaigns/types";

export default function useTemplateMessageEditor({
  isEditable,
  form: { getValues, setValue, formState, subscribe },
  customVariables,
  onFocus,
}: {
  isEditable: boolean;
  form: UseFormReturn<Exclude<TemplateSchemaType, VoiceTemplateSchemaType>>;
  customVariables: DetailedCampaign["custom_variable_keys"];
  onFocus: () => void;
}) {
  const initialData = getValues();

  const templateMessageEditor = useEditor({
    editable: isEditable,
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      AiVariableExtension,
      VariableExtension(customVariables),
    ],
    content: initialData
      ? parseTemplateText(
          initialData.message,
          customVariables,
          initialData.ai_descriptors,
        )
      : null,
    onUpdate: ({ editor }) => {
      const editorJSON = editor.getJSON();
      const newMessage = stringifyTemplateContent(editorJSON);
      const newAiDescriptors = extractAiDescriptors(editorJSON.content);

      if (newMessage !== getValues("message")) {
        setValue("message", newMessage, {
          shouldTouch: true,
          shouldValidate: formState.isSubmitted,
          shouldDirty: true,
        });
      }
      if (!areObjectsEqual(newAiDescriptors, getValues("ai_descriptors"))) {
        setValue("ai_descriptors", newAiDescriptors, {
          shouldTouch: true,
          shouldDirty: true,
        });
      }
    },
    onFocus,
    editorProps: {
      attributes: {
        class: clsx(
          "text-md lg:text-sm h-full focus:outline-none [&_.node-variable]:align-middle [&_.node-aiVariable]:align-middle",
          !isEditable && "text-black-500",
        ),
      },
    },
    shouldRerenderOnTransaction: false,
  });

  //this is used to reset editor state based on external changes
  useEffect(() => {
    return subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        const editorJSON = templateMessageEditor.getJSON();
        const editorAiDescriptors = extractAiDescriptors(editorJSON.content);
        const editorMessage = stringifyTemplateContent(editorJSON);
        if (
          "message" in values &&
          (editorMessage !== values.message ||
            (values.ai_descriptors &&
              !areObjectsEqual(editorAiDescriptors, values.ai_descriptors)))
        ) {
          templateMessageEditor.commands.setContent(
            parseTemplateText(
              values.message,
              customVariables,
              values.ai_descriptors,
            ),
          );
        }
      },
    });
  }, [subscribe]);

  return { templateMessageEditor };
}
